import { useState, useEffect, useRef, useCallback, useMemo } from 'preact/hooks';
import { showToast } from '../components/Toast';
import { T } from '../styles/tokens';
import { Button, Spinner } from '../components/ui';

/* ═══ Types ═══ */
interface ScanCheck {
  id: string;
  category: string;
  check_name: string;
  status: 'pass' | 'warning' | 'critical' | 'info' | 'error';
  severity: string;
  details: string;
}

interface ScanResult {
  id: string;
  url: string;
  score: number;
  status: string;
  checks: ScanCheck[];
  completedAt: string;
}

interface HistoryItem {
  id: string;
  url: string;
  score: number;
  status: string;
  completedAt: string;
}

/* ═══ Constants ═══ */
const API_BASE = '/api/v1';

const CATEGORY_LABELS: Record<string, { label: string; icon: string }> = {
 ssl: { label: 'SSL / TLS', icon: '' },
 headers: { label: 'Headers de Segurança', icon: '' },
 dns: { label: 'DNS', icon: '' },
 redirect: { label: 'Redirecionamentos', icon: '' },
 cookies: { label: 'Cookies', icon: '' },
 server_leak: { label: 'Server Info', icon: '' },
};

const STATUS_COLORS: Record<string, string> = {
  pass: T.success,
  warning: T.warning,
  critical: T.danger,
  info: T.primary,
  error: T.danger,
};

const STATUS_ICONS: Record<string, string> = {
 pass: '',
 warning: '',
 critical: '',
 info: '',
 error: '',
};

type TabId = 'result' | 'history' | 'compare';

/* ═══ Recommendations Map ═══ */
interface Recommendation {
  checkPattern: string;
  category: string;
  title: string;
  description: string;
  docsUrl?: string;
}

const RECOMMENDATIONS_MAP: Recommendation[] = [
  // SSL
  { checkPattern: 'ssl_valid', category: 'ssl', title: 'Renove o certificado SSL', description: 'Seu certificado SSL está expirado ou próximo da expiração. Renove-o o mais rápido possível.', docsUrl: 'https://letsencrypt.org/getting-started/' },
  { checkPattern: 'ssl_expiry', category: 'ssl', title: 'Certificado expirando em breve', description: 'Configure renovação automática do certificado SSL para evitar downtime.', docsUrl: 'https://certbot.eff.org/' },
  { checkPattern: 'hsts', category: 'ssl', title: 'Habilite HSTS', description: 'Adicione o header Strict-Transport-Security para forçar conexões HTTPS.', docsUrl: 'https://developer.mozilla.org/en-US/docs/Web/HTTP/Headers/Strict-Transport-Security' },
  { checkPattern: 'tls_version', category: 'ssl', title: 'Use TLS 1.2+', description: 'Desabilite TLS 1.0 e 1.1 — são considerados inseguros.', docsUrl: 'https://ssl-config.mozilla.org/' },
  { checkPattern: 'ssl', category: 'ssl', title: 'Corrija a configuração SSL', description: 'Revise a configuração SSL/TLS do seu servidor para garantir segurança.', docsUrl: 'https://www.ssllabs.com/ssltest/' },
  // Headers
  { checkPattern: 'content_security_policy', category: 'headers', title: 'Adicione Content-Security-Policy', description: 'CSP previne ataques XSS e injeção de dados. Defina políticas para scripts, estilos e fontes.', docsUrl: 'https://developer.mozilla.org/en-US/docs/Web/HTTP/CSP' },
  { checkPattern: 'x_frame_options', category: 'headers', title: 'Adicione X-Frame-Options', description: 'Previna clickjacking adicionando X-Frame-Options: DENY ou SAMEORIGIN.', docsUrl: 'https://developer.mozilla.org/en-US/docs/Web/HTTP/Headers/X-Frame-Options' },
  { checkPattern: 'x_content_type', category: 'headers', title: 'Adicione X-Content-Type-Options', description: 'Previna MIME sniffing com X-Content-Type-Options: nosniff.', docsUrl: 'https://developer.mozilla.org/en-US/docs/Web/HTTP/Headers/X-Content-Type-Options' },
  { checkPattern: 'referrer_policy', category: 'headers', title: 'Configure Referrer-Policy', description: 'Controle quanta informação de referrer é enviada com requisições.', docsUrl: 'https://developer.mozilla.org/en-US/docs/Web/HTTP/Headers/Referrer-Policy' },
  { checkPattern: 'permissions_policy', category: 'headers', title: 'Adicione Permissions-Policy', description: 'Restrinja acesso a APIs do navegador como câmera, geolocalização, etc.', docsUrl: 'https://developer.mozilla.org/en-US/docs/Web/HTTP/Headers/Permissions-Policy' },
  { checkPattern: 'header', category: 'headers', title: 'Adicione headers de segurança', description: 'Headers de segurança HTTP protegem contra diversos tipos de ataques.', docsUrl: 'https://owasp.org/www-project-secure-headers/' },
  // DNS
  { checkPattern: 'caa', category: 'dns', title: 'Configure CAA record', description: 'CAA records controlam quais CAs podem emitir certificados para seu domínio.', docsUrl: 'https://letsencrypt.org/docs/caa/' },
  { checkPattern: 'dnssec', category: 'dns', title: 'Habilite DNSSEC', description: 'DNSSEC protege contra envenenamento de cache DNS e ataques man-in-the-middle.', docsUrl: 'https://www.cloudflare.com/dns/dnssec/how-dnssec-works/' },
  { checkPattern: 'dns', category: 'dns', title: 'Melhore configuração DNS', description: 'Revise as configurações DNS para maior segurança e performance.', docsUrl: 'https://www.cloudflare.com/learning/dns/dns-security/' },
  // Cookies
  { checkPattern: 'cookie_secure', category: 'cookies', title: 'Adicione flag Secure', description: 'Cookies sem flag Secure podem ser interceptados em conexões HTTP.', docsUrl: 'https://developer.mozilla.org/en-US/docs/Web/HTTP/Cookies#restrict_access_to_cookies' },
  { checkPattern: 'cookie_httponly', category: 'cookies', title: 'Adicione flag HttpOnly', description: 'Cookies sem HttpOnly podem ser acessados via JavaScript, facilitando XSS.', docsUrl: 'https://owasp.org/www-community/HttpOnly' },
  { checkPattern: 'cookie_samesite', category: 'cookies', title: 'Use SameSite=Strict', description: 'SameSite previne ataques CSRF controlando envio de cookies em requisições cross-site.', docsUrl: 'https://developer.mozilla.org/en-US/docs/Web/HTTP/Headers/Set-Cookie#samesitesamesite-value' },
  { checkPattern: 'cookie', category: 'cookies', title: 'Corrija configuração de cookies', description: 'Cookies devem ter flags Secure, HttpOnly e SameSite para segurança adequada.' },
  // Server leak
  { checkPattern: 'server_header', category: 'server_leak', title: 'Remova header Server', description: 'O header Server expõe informações sobre seu servidor web. Remova-o ou ofusque-o.', docsUrl: 'https://owasp.org/www-project-web-security-testing-guide/latest/4-Web_Application_Security_Testing/01-Information_Gathering/02-Fingerprint_Web_Server' },
  { checkPattern: 'x_powered_by', category: 'server_leak', title: 'Remova X-Powered-By', description: 'O header X-Powered-By expõe a tecnologia do backend. Remova-o.', docsUrl: 'https://owasp.org/www-project-secure-headers/#x-powered-by' },
  { checkPattern: 'server_leak', category: 'server_leak', title: 'Oculte informações do servidor', description: 'Evite expor versões e tecnologias do servidor em headers HTTP.' },
  // Redirect
  { checkPattern: 'https_redirect', category: 'redirect', title: 'Force HTTPS redirect', description: 'Garanta que todas as requisições HTTP são redirecionadas para HTTPS (301).', docsUrl: 'https://developer.mozilla.org/en-US/docs/Web/HTTP/Redirections' },
  { checkPattern: 'redirect_chain', category: 'redirect', title: 'Evite redirect chain', description: 'Múltiplos redirecionamentos em cadeia afetam performance e segurança. Use redirecionamento direto.' },
  { checkPattern: 'redirect', category: 'redirect', title: 'Corrija redirecionamentos', description: 'Redirecionamentos devem ser seguros (HTTPS) e diretos (sem cadeia).' },
];

function getRecommendationsForChecks(checks: ScanCheck[]): (Recommendation & { severity: string; status: string })[] {
  const results: (Recommendation & { severity: string; status: string })[] = [];
  const used = new Set<string>();

  for (const check of checks) {
    if (check.status === 'pass' || check.status === 'info') continue;
    const checkId = check.id.toLowerCase();
    const checkName = check.check_name.toLowerCase();

    // Find best matching recommendation
    let bestMatch: Recommendation | null = null;
    for (const rec of RECOMMENDATIONS_MAP) {
      if (used.has(rec.title)) continue;
      if (checkId.includes(rec.checkPattern) || checkName.includes(rec.checkPattern)) {
        bestMatch = rec;
        break;
      }
    }
    // Fallback: match by category
    if (!bestMatch) {
      for (const rec of RECOMMENDATIONS_MAP) {
        if (used.has(rec.title)) continue;
        if (rec.category === check.category && !used.has(rec.title)) {
          bestMatch = rec;
          break;
        }
      }
    }

    if (bestMatch) {
      used.add(bestMatch.title);
      results.push({ ...bestMatch, severity: check.severity, status: check.status });
    }
  }

  // Sort: critical first, then warning
  const order: Record<string, number> = { critical: 0, error: 0, warning: 1, info: 2, pass: 3 };
  results.sort((a, b) => (order[a.status] ?? 2) - (order[b.status] ?? 2));
  return results;
}

/* ═══ Helpers ═══ */
function getGrade(score: number): { grade: string; color: string } {
  if (score >= 90) return { grade: 'A+', color: T.success };
  if (score >= 80) return { grade: 'A', color: T.success };
  if (score >= 70) return { grade: 'B', color: T.warning };
  if (score >= 60) return { grade: 'C', color: '#FB923C' };
  if (score >= 50) return { grade: 'D', color: '#F87171' };
  return { grade: 'F', color: T.danger };
}

function getScoreColor(score: number): string {
  if (score >= 80) return T.success;
  if (score >= 60) return T.warning;
  return T.danger;
}

function isValidUrl(str: string): boolean {
  try {
    const u = new URL(str);
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
}

/* ═══ Sparkline SVG Component ═══ */
function Sparkline({ scores }: { scores: number[] }) {
  if (scores.length < 2) return null;
  const w = 140, h = 36, pad = 2;
  const min = Math.min(...scores) - 5;
  const max = Math.max(...scores) + 5;
  const range = max - min || 1;
  const points = scores.map((s, i) => {
    const x = pad + (i / (scores.length - 1)) * (w - pad * 2);
    const y = h - pad - ((s - min) / range) * (h - pad * 2);
    return `${x},${y}`;
  });
  const last = scores[scores.length - 1];
  const color = getScoreColor(last ?? 0);
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} style={{ display: 'block' }}>
      <polyline
        points={points.join(' ')}
        fill="none" stroke={color} stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
      />
      {/* dot on last point */}
      {(() => {
        const lx = pad + ((scores.length - 1) / (scores.length - 1)) * (w - pad * 2);
        const ly = h - pad - (((last ?? 0) - min) / range) * (h - pad * 2);
        return <circle cx={lx} cy={ly} r="3" fill={color} />;
      })()}
    </svg>
  );
}

/* ═══ Skeleton ═══ */
function SkeletonRow() {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12,
      padding: '12px 14px', borderRadius: 10,
      background: T.bgCard,
      border: `1px solid ${T.bgCard}`,
      animation: 'pulse 1.5s ease-in-out infinite',
    }}>
      <div style={{ width: 36, height: 20, borderRadius: 4, background: T.border }} />
      <div style={{ flex: 1 }}>
        <div style={{ width: '60%', height: 13, borderRadius: 4, background: T.border, marginBottom: 6 }} />
        <div style={{ width: '30%', height: 11, borderRadius: 4, background: T.bgCard }} />
      </div>
      <div style={{ width: 60, height: 20, borderRadius: 6, background: T.bgCard }} />
    </div>
  );
}

/* ═══ Circular Score Component ═══ */
function CircularScore({ score, animated }: { score: number; animated: boolean }) {
  const [display, setDisplay] = useState(0);
  const radius = 70;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (display / 100) * circumference;
  const { grade, color } = getGrade(display);

  useEffect(() => {
    if (!animated) { setDisplay(score); return; }
    setDisplay(0);
    const duration = 1500;
    const start = performance.now();
    const step = (now: number) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.round(eased * score));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [score, animated]);

  return (
    <div style={{ textAlign: 'center', animation: 'fadeIn 0.6s ease' }}>
      <svg width="180" height="180" viewBox="0 0 180 180" style={{ filter: `drop-shadow(0 0 20px ${color}40)` }}>
        <circle cx="90" cy="90" r={radius} fill="none" stroke={T.border} stroke-width="10" />
        <circle
          cx="90" cy="90" r={radius} fill="none"
          stroke={color} stroke-width="10" stroke-linecap="round"
          stroke-dasharray={circumference} stroke-dashoffset={offset}
          transform="rotate(-90 90 90)"
          style={{ transition: 'stroke-dashoffset 0.1s linear' }}
        />
        <text x="90" y="82" text-anchor="middle" fill="#E8EEF9" font-size="42" font-weight="800">{display}</text>
        <text x="90" y="110" text-anchor="middle" fill={color} font-size="22" font-weight="700">{grade}</text>
      </svg>
      <div style={{ marginTop: 8, fontSize: 14, color: T.textSecondary }}>
 Pontuação de Segurança
      </div>
    </div>
  );
}

/* ═══ Category Card ═══ */
function CategoryCard({ category, checks, delay }: { category: string; checks: ScanCheck[]; delay: number }) {
  const [expanded, setExpanded] = useState(false);
 const meta = CATEGORY_LABELS[category] || { label: category, icon: '' };

  const worstStatus = checks.reduce((worst, c) => {
    const order = ['critical', 'error', 'warning', 'info', 'pass'];
    return order.indexOf(c.status) < order.indexOf(worst) ? c.status : worst;
  }, 'pass' as string);

  const borderColor = STATUS_COLORS[worstStatus] || T.primary;

  return (
    <div
      onClick={() => setExpanded(!expanded)}
      style={{
        background: T.bgCard,
        border: `1px solid ${borderColor}30`,
        borderLeft: `3px solid ${borderColor}`,
        borderRadius: 12,
        padding: '14px 18px',
        cursor: 'pointer',
        transition: 'all 0.2s',
        animation: `slideUp 0.4s ease ${delay}ms both`,
      }}
      onMouseEnter={(e: any) => { e.currentTarget.style.background = T.border; }}
      onMouseLeave={(e: any) => { e.currentTarget.style.background = T.bgCard; }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 20 }}>{meta.icon}</span>
          <span style={{ fontSize: 15, fontWeight: 600, color: T.text }}>{meta.label}</span>
          <span style={{
            fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 6,
            background: `${borderColor}20`, color: borderColor,
          }}>
            {STATUS_ICONS[worstStatus]} {worstStatus.toUpperCase()}
          </span>
        </div>
        <span style={{ fontSize: 12, color: T.textSecondary, transform: expanded ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s' }}>▼</span>
      </div>

      {expanded && (
        <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {checks.map((c) => (
            <div key={c.id} style={{
              padding: '10px 14px', borderRadius: 8,
              background: `${STATUS_COLORS[c.status]}08`,
              border: `1px solid ${STATUS_COLORS[c.status]}15`,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <span>{STATUS_ICONS[c.status]}</span>
                <span style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{c.check_name}</span>
                <span style={{
                  fontSize: 10, padding: '1px 6px', borderRadius: 4,
                  background: `${STATUS_COLORS[c.status]}20`, color: STATUS_COLORS[c.status],
                  fontWeight: 700, marginLeft: 'auto',
                }}>
                  {c.severity}
                </span>
              </div>
              <div style={{ fontSize: 12, color: T.textSecondary, lineHeight: 1.5 }}>{c.details}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ═══ Recommendations Section ═══ */
function RecommendationsSection({ checks }: { checks: ScanCheck[] }) {
  const recs = useMemo(() => getRecommendationsForChecks(checks), [checks]);
  const [copied, setCopied] = useState(false);

  if (recs.length === 0) {
    return (
      <div style={{
        background: T.successBg, border: `1px solid ${T.successBg}`,
        borderRadius: 14, padding: '20px', textAlign: 'center', marginTop: 20,
        animation: 'fadeIn 0.5s ease',
      }}>
 <div style={{ fontSize: 32, marginBottom: 8 }}></div>
        <div style={{ fontSize: 15, fontWeight: 600, color: T.success }}>Nenhuma recomendação pendente!</div>
        <div style={{ fontSize: 13, color: T.textSecondary, marginTop: 4 }}>Todos os checks passaram. Excelente!</div>
      </div>
    );
  }

  const copyAll = () => {
    const text = recs.map((r, i) => {
      let line = `${i + 1}. [${r.status.toUpperCase()}] ${r.title}\n   ${r.description}`;
      if (r.docsUrl) line += `\n   Docs: ${r.docsUrl}`;
      return line;
    }).join('\n\n');
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div style={{
      background: T.bgCard, border: `1px solid ${T.border}`,
      borderRadius: 14, padding: '20px', marginTop: 20,
      animation: 'fadeIn 0.5s ease 0.3s both',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <h3 style={{ fontSize: 16, fontWeight: 700, color: T.text, margin: 0 }}>
 Recomendações ({recs.length})
        </h3>
        <Button variant={copied ? 'success' : 'ghost'} size="sm" onClick={copyAll}>
 {copied ? 'Copiado!' : 'Copiar todas'}
        </Button>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {recs.map((rec, i) => {
          const color = STATUS_COLORS[rec.status] || T.warning;
          return (
            <div key={i} style={{
              padding: '12px 16px', borderRadius: 10,
              background: `${color}06`, border: `1px solid ${color}15`,
              borderLeft: `3px solid ${color}`,
              animation: `slideUp 0.3s ease ${i * 60}ms both`,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
 <span style={{ fontSize: 14 }}>{rec.status === 'critical' ? '' : ''}</span>
                <span style={{ fontSize: 13, fontWeight: 700, color: T.text }}>{rec.title}</span>
                <span style={{
                  fontSize: 10, padding: '1px 6px', borderRadius: 4, marginLeft: 'auto',
                  background: `${color}20`, color, fontWeight: 700,
                }}>
                  {rec.status.toUpperCase()}
                </span>
              </div>
              <div style={{ fontSize: 12, color: T.textSecondary, lineHeight: 1.5 }}>{rec.description}</div>
              {rec.docsUrl && (
                <a href={rec.docsUrl} target="_blank" rel="noopener noreferrer" style={{
                  fontSize: 11, color: T.primary, textDecoration: 'none', marginTop: 6, display: 'inline-block',
                }}>
 Documentação →
                </a>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ═══ Comparison View ═══ */
function ComparisonView({ scanA, scanB }: { scanA: ScanResult; scanB: ScanResult }) {
  const delta = scanB.score - scanA.score;
  const deltaColor = delta > 0 ? T.success : delta < 0 ? T.danger : T.textSecondary;

  // Build check map for comparison
  const checksA = new Map<string, ScanCheck>();
  const checksB = new Map<string, ScanCheck>();
  for (const c of scanA.checks) checksA.set(c.id, c);
  for (const c of scanB.checks) checksB.set(c.id, c);
  const allCheckIds = [...new Set([...checksA.keys(), ...checksB.keys()])];

  const improved: string[] = [];
  const worsened: string[] = [];
  const unchanged: string[] = [];

  const statusOrder: Record<string, number> = { pass: 0, info: 1, warning: 2, error: 3, critical: 4 };

  for (const id of allCheckIds) {
    const a = checksA.get(id);
    const b = checksB.get(id);
    if (!a || !b) { unchanged.push(id); continue; }
    const sa = statusOrder[a.status] ?? 2;
    const sb = statusOrder[b.status] ?? 2;
    if (sb < sa) improved.push(id);
    else if (sb > sa) worsened.push(id);
    else unchanged.push(id);
  }

  const renderCheck = (id: string) => {
    const a = checksA.get(id);
    const b = checksB.get(id);
    const check = b || a;
    if (!check) return null;
    return (
      <div key={id} style={{
        display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px',
        borderRadius: 6, background: T.bgCard, fontSize: 12,
      }}>
        <span>{a ? STATUS_ICONS[a.status] : '—'}</span>
        <span style={{ color: T.textSecondary }}>→</span>
        <span>{b ? STATUS_ICONS[b.status] : '—'}</span>
        <span style={{ color: T.text, fontWeight: 500 }}>{check.check_name}</span>
      </div>
    );
  };

  return (
    <div style={{ animation: 'fadeIn 0.5s ease' }}>
      {/* Score comparison */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 24,
        padding: 24, background: T.bgCard, borderRadius: 14,
        border: `1px solid ${T.border}`, marginBottom: 20,
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 11, color: T.textSecondary, marginBottom: 4 }}>Anterior</div>
          <div style={{ fontSize: 32, fontWeight: 800, color: getScoreColor(scanA.score) }}>{scanA.score}</div>
          <div style={{ fontSize: 11, color: T.textSecondary }}>
            {scanA.completedAt ? new Date(scanA.completedAt).toLocaleDateString('pt-BR') : ''}
          </div>
        </div>
        <div style={{
          fontSize: 24, fontWeight: 800, color: deltaColor,
          padding: '8px 16px', borderRadius: 10,
          background: `${deltaColor}10`,
        }}>
          {delta > 0 ? '+' : ''}{delta}
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 11, color: T.textSecondary, marginBottom: 4 }}>Recente</div>
          <div style={{ fontSize: 32, fontWeight: 800, color: getScoreColor(scanB.score) }}>{scanB.score}</div>
          <div style={{ fontSize: 11, color: T.textSecondary }}>
            {scanB.completedAt ? new Date(scanB.completedAt).toLocaleDateString('pt-BR') : ''}
          </div>
        </div>
      </div>

      {/* Changes */}
      {improved.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.success, marginBottom: 8 }}>
 Melhoraram ({improved.length})
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {improved.map(renderCheck)}
          </div>
        </div>
      )}
      {worsened.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.danger, marginBottom: 8 }}>
 Pioraram ({worsened.length})
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {worsened.map(renderCheck)}
          </div>
        </div>
      )}
      {improved.length === 0 && worsened.length === 0 && (
        <div style={{ textAlign: 'center', padding: 20, color: T.textSecondary, fontSize: 13 }}>
 Nenhuma mudança detectada entre os scans
        </div>
      )}
    </div>
  );
}

/* ═══ Main Page ═══ */
export function SecurityScanPage(_props: { path?: string }) {
  const [url, setUrl] = useState('');
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState<ScanResult | null>(null);
  const [showScore, setShowScore] = useState(false);
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const debounceRef = useRef<any>(null);
  const [urlValid, setUrlValid] = useState(true);
  const initSecTab = (() => {
    const seg = window.location.pathname.replace(/^\/security-scan\/?/, '').split('/')[0];
    return (['result', 'history', 'compare'] as TabId[]).includes(seg as TabId) ? seg as TabId : 'result';
  })();
  const [activeTab, setActiveTabRaw] = useState<TabId>(initSecTab);
  const setActiveTab = (t: TabId) => {
    setActiveTabRaw(t);
    const newPath = t === 'result' ? '/security-scan' : `/security-scan/${t}`;
    if (window.location.pathname !== newPath) window.history.replaceState(null, '', newPath);
  };
  const [historyFilter, setHistoryFilter] = useState('');
  const [compareA, setCompareA] = useState<ScanResult | null>(null);
  const [compareB, setCompareB] = useState<ScanResult | null>(null);
  const [compareLoading, setCompareLoading] = useState(false);
  const [selectedForCompare, setSelectedForCompare] = useState<string | null>(null);

  const validateUrl = useCallback((val: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setUrlValid(val === '' || isValidUrl(val));
    }, 300);
  }, []);

  const handleUrlChange = (e: any) => {
    const val = e.target.value;
    setUrl(val);
    validateUrl(val);
  };

  const fetchHistory = async () => {
    setHistoryLoading(true);
    try {
      const res = await fetch(`${API_BASE}/security/history?limit=50`, { credentials: 'include' });
      const data = await res.json();
      if (data.success) setHistory(data.data || []);
    } catch { /* ignore */ }
    setHistoryLoading(false);
  };

  useEffect(() => { fetchHistory(); }, []);

  const loadResult = async (id: string) => {
    try {
      const res = await fetch(`${API_BASE}/security/scan/${id}`, { credentials: 'include' });
      const data = await res.json();
      if (data.success && data.data) {
        setResult(data.data);
        setShowScore(true);
        setActiveTab('result');
      }
    } catch {
      showToast('Erro ao carregar resultado', 'error');
    }
  };

  const loadForCompare = async (id: string) => {
    setCompareLoading(true);
    try {
      const res = await fetch(`${API_BASE}/security/scan/${id}`, { credentials: 'include' });
      const data = await res.json();
      if (data.success && data.data) {
        if (!compareA) {
          setCompareA(data.data);
          setSelectedForCompare(null);
          showToast('Primeiro scan selecionado. Agora selecione o segundo.', 'info');
        } else {
          setCompareB(data.data);
          setSelectedForCompare(null);
          setActiveTab('compare');
        }
      }
    } catch {
      showToast('Erro ao carregar scan para comparação', 'error');
    }
    setCompareLoading(false);
  };

  const startScan = async () => {
    if (!url || !isValidUrl(url)) {
      showToast('URL inválida. Use o formato https://exemplo.com', 'error');
      return;
    }
    setScanning(true);
    setResult(null);
    setShowScore(false);
    setActiveTab('result');

    try {
      const res = await fetch(`${API_BASE}/security/scan`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url }),
      });

      if (res.status === 429) {
        showToast('Rate limit atingido. Aguarde antes de escanear novamente.', 'error');
        setScanning(false);
        return;
      }

      const data = await res.json();
      if (!data.success) {
        showToast(data.error?.message || 'Erro ao iniciar scan', 'error');
        setScanning(false);
        return;
      }

      const scanId = data.data?.id;
      if (!scanId) { setScanning(false); return; }

      let attempts = 0;
      const poll = setInterval(async () => {
        attempts++;
        if (attempts > 60) {
          clearInterval(poll);
          showToast('Timeout: O scan demorou demais', 'error');
          setScanning(false);
          return;
        }
        try {
          const r = await fetch(`${API_BASE}/security/scan/${scanId}`, { credentials: 'include' });
          const d = await r.json();
          if (d.success && d.data && (d.data.status === 'completed' || d.data.status === 'done')) {
            clearInterval(poll);
            setResult(d.data);
            setScanning(false);
            setShowScore(true);
            fetchHistory();
          } else if (d.data?.status === 'error' || d.data?.status === 'failed') {
            clearInterval(poll);
            showToast('O scan falhou. Tente novamente.', 'error');
            setScanning(false);
          }
        } catch { /* retry */ }
      }, 2000);
    } catch (e: any) {
      showToast(e.message || 'Erro de conexão', 'error');
      setScanning(false);
    }
  };

  // Group checks by category
  const checksByCategory: Record<string, ScanCheck[]> = {};
  if (result?.checks) {
    for (const c of result.checks) {
      if (!checksByCategory[c.category]) checksByCategory[c.category] = [];
      checksByCategory[c.category]!.push(c);
    }
  }

  const categoryOrder = ['ssl', 'headers', 'dns', 'redirect', 'cookies', 'server_leak'];

  // Filtered history
  const filteredHistory = useMemo(() => {
    if (!historyFilter) return history;
    const lower = historyFilter.toLowerCase();
    return history.filter(h => h.url.toLowerCase().includes(lower));
  }, [history, historyFilter]);

  // Unique URLs for filter
  const uniqueUrls = useMemo(() => [...new Set(history.map(h => h.url))], [history]);

  // Sparkline data: scores for the current result URL from history
  const sparklineScores = useMemo(() => {
    if (!result?.url) return [];
    return history
      .filter(h => h.url === result.url && (h.status === 'completed' || h.status === 'done'))
      .slice(0, 10)
      .reverse()
      .map(h => h.score);
  }, [history, result?.url]);

  // Tab styles
  const tabStyle = (id: TabId): any => ({
    padding: '8px 18px', borderRadius: 8, border: 'none', fontSize: 13, fontWeight: 600,
    cursor: 'pointer', transition: 'all 0.2s',
    background: activeTab === id ? T.primary : T.bgCard,
    color: activeTab === id ? '#fff' : T.textSecondary,
  });

  return (
    <div style={{ padding: '28px 16px', maxWidth: 800, margin: '0 auto' }}>
      {/* Back to DevMode */}
      <a
        href="/dev-mode"
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '8px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600,
          background: T.bgCard, color: T.textSecondary,
          border: `1px solid ${T.border}`, textDecoration: 'none',
          marginBottom: 20, cursor: 'pointer', transition: 'all 0.15s',
        }}
      >
        ← Voltar ao Modo Dev
      </a>

      {/* Header */}
      <div style={{ marginBottom: 24, animation: 'fadeIn 0.5s ease', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <h1 style={{ fontSize: 26, fontWeight: 800, color: T.text, margin: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
 Teste de Segurança
        </h1>
        {result && showScore && !scanning && (
          <span style={{
            fontSize: 13, fontWeight: 800, padding: '4px 12px', borderRadius: 8,
            background: `${getScoreColor(result.score)}20`, color: getScoreColor(result.score),
            animation: 'fadeIn 0.3s ease',
          }}>
            {getGrade(result.score).grade} — {result.score}/100
          </span>
        )}
      </div>
      <p style={{ fontSize: 14, color: T.textSecondary, marginTop: -16, marginBottom: 24 }}>
        Analise a segurança de qualquer site em segundos
      </p>

      {/* Input Section */}
      <div style={{
        background: T.bgCard,
        border: `1px solid ${T.border}`,
        borderRadius: 14, padding: '20px', marginBottom: 24,
        animation: 'fadeIn 0.5s ease 0.1s both',
      }}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <input
            type="url"
            value={url}
            onInput={handleUrlChange}
            placeholder="Ex: https://meusite.com.br"
            disabled={scanning}
            onKeyDown={(e: any) => { if (e.key === 'Enter' && !scanning) startScan(); }}
            style={{
              flex: 1, minWidth: 0, width: '100%', padding: '12px 16px',
              background: T.bgCard,
              border: `1px solid ${!urlValid ? T.danger : T.border}`,
              borderRadius: 10, color: T.text, fontSize: 15, outline: 'none',
              transition: 'border-color 0.2s', boxSizing: 'border-box',
            }}
          />
          <Button variant="primary" size="lg" onClick={startScan} disabled={scanning || !url || !urlValid} loading={scanning}>
            {scanning ? 'Escaneando...' : 'Escanear'}
          </Button>
        </div>
        {!urlValid && url && (
          <div style={{ fontSize: 12, color: T.danger, marginTop: 8 }}>
            URL inválida. Use o formato: https://exemplo.com
          </div>
        )}
      </div>

      {/* Scanning Progress */}
      {scanning && (
        <div style={{ textAlign: 'center', padding: 40, animation: 'fadeIn 0.4s ease' }}>
          <Spinner size="lg" inline />
          <div style={{ fontSize: 16, fontWeight: 600, color: T.text }}>Escaneando...</div>
          <div style={{ fontSize: 13, color: T.textSecondary, marginTop: 4 }}>Verificando segurança de {url}</div>
        </div>
      )}

      {/* Tabs */}
      {!scanning && (
        <div style={{ display: 'flex', gap: 6, marginBottom: 20, flexWrap: 'wrap' }}>
          <Button variant={activeTab === 'result' ? 'primary' : 'ghost'} size="sm" onClick={() => setActiveTab('result')}>
 Resultado
          </Button>
          <Button variant={activeTab === 'history' ? 'primary' : 'ghost'} size="sm" onClick={() => setActiveTab('history')}>
 Histórico
          </Button>
          <Button variant={activeTab === 'compare' ? 'primary' : 'ghost'} size="sm" onClick={() => setActiveTab('compare')}>
 Comparação
          </Button>
        </div>
      )}

      {/* ═══ TAB: Result ═══ */}
      {activeTab === 'result' && !scanning && (
        <>
          {result && showScore ? (
            <div style={{ marginBottom: 32, animation: 'fadeIn 0.5s ease' }}>
              <CircularScore score={result.score} animated={true} />

              {/* Sparkline */}
              {sparklineScores.length >= 2 && (
                <div style={{ textAlign: 'center', marginTop: 12 }}>
                  <div style={{ fontSize: 11, color: T.textSecondary, marginBottom: 4 }}>Evolução do score ({sparklineScores.length} scans)</div>
                  <div style={{ display: 'inline-block' }}>
                    <Sparkline scores={sparklineScores} />
                  </div>
                </div>
              )}

              {/* Category Cards with stagger */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 28 }}>
                {categoryOrder.map((cat, i) => {
                  const checks = checksByCategory[cat];
                  if (!checks || checks.length === 0) return null;
                  return <CategoryCard key={cat} category={cat} checks={checks} delay={i * 80} />;
                })}
              </div>

              {/* Recommendations */}
              {result.checks && <RecommendationsSection checks={result.checks} />}
            </div>
          ) : (
            <div style={{
              textAlign: 'center', padding: 48,
              background: T.bgCard, borderRadius: 14,
              border: `1px solid ${T.bgCard}`,
            }}>
 <div style={{ fontSize: 48, marginBottom: 12 }}></div>
              <div style={{ fontSize: 16, fontWeight: 600, color: T.text }}>Nenhum resultado ainda</div>
              <div style={{ fontSize: 13, color: T.textSecondary, marginTop: 6 }}>
                Digite uma URL acima e clique em "Escanear" para começar
              </div>
            </div>
          )}
        </>
      )}

      {/* ═══ TAB: History ═══ */}
      {activeTab === 'history' && !scanning && (
        <div style={{
          background: T.bgCard,
          border: `1px solid ${T.border}`,
          borderRadius: 14, padding: '20px',
          animation: 'fadeIn 0.3s ease',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: T.text, margin: 0 }}>
 Histórico de Scans
            </h3>
            {/* URL filter */}
            {uniqueUrls.length > 1 && (
              <select
                value={historyFilter}
                onChange={(e: any) => setHistoryFilter(e.target.value)}
                style={{
                  padding: '6px 10px', borderRadius: 8, fontSize: 12,
                  background: T.bgCard, color: T.text,
                  border: `1px solid ${T.bgInputHover}`, outline: 'none',
                  maxWidth: 220,
                }}
              >
                <option value="" style={{ background: T.bgSecondary }}>Todos os URLs</option>
                {uniqueUrls.map(u => (
                  <option key={u} value={u} style={{ background: T.bgSecondary }}>
                    {u.length > 35 ? u.slice(0, 35) + '…' : u}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Compare mode indicator */}
          {compareA && !compareB && (
            <div style={{
              padding: '8px 12px', borderRadius: 8, marginBottom: 12,
              background: T.primaryBg, border: `1px solid ${T.primaryBgStrong}`,
              fontSize: 12, color: '#7BA3FF',
            }}>
 Selecionado: <strong>{compareA.url}</strong> (score {compareA.score}). Clique "Comparar" em outro scan.
              <Button variant="ghost" size="xs" onClick={() => { setCompareA(null); setCompareB(null); }}>
                Cancelar
              </Button>
            </div>
          )}

          {historyLoading ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {[0,1,2,3,4].map(i => <SkeletonRow key={i} />)}
            </div>
          ) : filteredHistory.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 32 }}>
 <div style={{ fontSize: 40, marginBottom: 8 }}></div>
              <div style={{ fontSize: 14, fontWeight: 600, color: T.text }}>
                {historyFilter ? 'Nenhum scan encontrado para esse URL' : 'Nenhum scan realizado ainda'}
              </div>
              <div style={{ fontSize: 12, color: T.textSecondary, marginTop: 4 }}>
                {historyFilter ? 'Tente outro filtro' : 'Escaneie um site para começar'}
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {filteredHistory.map((h) => {
                const { color } = getGrade(h.score);
                return (
                  <div
                    key={h.id}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '10px 14px', borderRadius: 10,
                      background: T.bgCard,
                      border: `1px solid ${T.bgCard}`,
                      transition: 'all 0.15s',
                    }}
                    onMouseEnter={(e: any) => { e.currentTarget.style.background = T.bgCardHover; }}
                    onMouseLeave={(e: any) => { e.currentTarget.style.background = T.bgCard; }}
                  >
                    <span
                      onClick={() => loadResult(h.id)}
                      style={{
                        fontSize: 16, fontWeight: 800, color, minWidth: 36, textAlign: 'center', cursor: 'pointer',
                      }}
                    >{h.score}</span>
                    <div style={{ flex: 1, overflow: 'hidden', cursor: 'pointer' }} onClick={() => loadResult(h.id)}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: T.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {h.url}
                      </div>
                      <div style={{ fontSize: 11, color: T.textSecondary }}>
                        {h.completedAt ? new Date(h.completedAt).toLocaleString('pt-BR') : h.status}
                      </div>
                    </div>
                    <Button variant="ghost"
                      onClick={() => loadForCompare(h.id)}
                      disabled={compareLoading}
                      title="Selecionar para comparação"
                      style={{
                        padding: '4px 10px', borderRadius: 6, border: `1px solid ${T.borderHover}`,
                        background: T.bgCard, color: T.textSecondary, fontSize: 11,
                        fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all 0.15s',
                      }}
                      onMouseEnter={(e: any) => { e.currentTarget.style.background = T.primaryBg; e.currentTarget.style.color = '#7BA3FF'; }}
                      onMouseLeave={(e: any) => { e.currentTarget.style.background = T.bgCard; e.currentTarget.style.color = T.textSecondary; }}
                    >
 Comparar
                    </Button>
                    <span style={{
                      fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 6,
                      background: h.status === 'completed' || h.status === 'done' ? T.successBg : T.warningBg,
                      color: h.status === 'completed' || h.status === 'done' ? T.success : T.warning,
                    }}>
                      {h.status === 'completed' || h.status === 'done' ? 'OK' : h.status}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ═══ TAB: Compare ═══ */}
      {activeTab === 'compare' && !scanning && (
        <div style={{
          background: T.bgCard,
          border: `1px solid ${T.border}`,
          borderRadius: 14, padding: '20px',
          animation: 'fadeIn 0.3s ease',
        }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, color: T.text, margin: '0 0 16px 0' }}>
 Comparação de Scans
          </h3>
          {compareA && compareB ? (
            <>
              <ComparisonView scanA={compareA} scanB={compareB} />
              <Button variant="ghost" size="sm" onClick={() => { setCompareA(null); setCompareB(null); setActiveTab('history'); }} style={{ marginTop: 16 }}>
 Nova comparação
              </Button>
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: 32 }}>
 <div style={{ fontSize: 40, marginBottom: 8 }}></div>
              <div style={{ fontSize: 14, fontWeight: 600, color: T.text }}>Selecione 2 scans para comparar</div>
              <div style={{ fontSize: 12, color: T.textSecondary, marginTop: 6 }}>
                Vá ao tab "Histórico" e clique "Comparar" em dois scans diferentes
              </div>
              <Button onClick={() => setActiveTab('history')} style={{ marginTop: 16 }}>
                Ir ao Histórico
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Animations */}
      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes slideUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
        @media (max-width: 480px) {
          .scan-input-wrap { flex-direction: column !important; }
        }
      `}</style>
    </div>
  );
}
